import { useState } from 'react';
import { Ban, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useConfirm } from '@/hooks/use-confirm';
import { formatDateTime, getTokenStatus, tokenKindLabel } from '../format';
import { useMcpTokens, useRevokeAllMcpTokens, useRevokeMcpToken } from '../hooks/use-mcp';
import type { McpTokenItem } from '../types';
import { TableStateRow } from './table-state-row';
import { ScopeBadges, TokenStatusBadge } from './token-badges';

interface TokensTableProps {
  /** true — токены всех админов (mcp.manage), с колонкой email. */
  all: boolean;
  /** Дополнительные действия в панели над таблицей (например, «Создать токен»). */
  actions?: React.ReactNode;
}

/** Таблица MCP-токенов с отзывом по одному и «Отозвать все» (для своих). */
export function TokensTable({ all, actions }: TokensTableProps) {
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const { data, isLoading, error } = useMcpTokens(all, includeRevoked);
  const revokeOne = useRevokeMcpToken();
  const revokeAll = useRevokeAllMcpTokens();
  const { confirm, ConfirmationDialog } = useConfirm();
  const tokens = data ?? [];
  const checkboxId = all ? 'mcp-all-include-revoked' : 'mcp-own-include-revoked';

  const handleRevoke = async (token: McpTokenItem) => {
    const ok = await confirm({
      title: `Отозвать токен «${token.name}»?`,
      description: all
        ? `Токен администратора ${token.adminEmail} перестанет работать сразу. Клиенту придётся подключиться заново.`
        : 'Токен перестанет работать сразу. Клиенту придётся подключиться заново.',
      confirmText: 'Отозвать',
      variant: 'destructive',
    });
    if (ok) {
      revokeOne.mutate(token.id);
    }
  };

  const handleRevokeAll = async () => {
    const ok = await confirm({
      title: 'Отозвать все свои MCP-токены?',
      description: 'Все подключённые ИИ-клиенты (включая вход через браузер) потеряют доступ.',
      confirmText: 'Отозвать все',
      variant: 'destructive',
    });
    if (ok) {
      revokeAll.mutate();
    }
  };

  const activeCount = tokens.filter((token) => getTokenStatus(token) === 'active').length;
  const showRows = !isLoading && !error && tokens.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id={checkboxId}
            checked={includeRevoked}
            onCheckedChange={(checked) => setIncludeRevoked(checked === true)}
          />
          <Label htmlFor={checkboxId} className="text-sm font-normal">
            Показывать отозванные и истёкшие
          </Label>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          {!all ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 text-destructive"
              onClick={() => void handleRevokeAll()}
              disabled={activeCount === 0 || revokeAll.isPending}
            >
              {revokeAll.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
              Отозвать все
            </Button>
          ) : null}
          {actions}
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Название</TableHead>
              {all ? <TableHead>Администратор</TableHead> : null}
              <TableHead>Тип</TableHead>
              <TableHead>Доступ</TableHead>
              <TableHead>Последнее использование</TableHead>
              <TableHead>Истекает</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {!showRows ? (
              <TableStateRow colSpan={all ? 8 : 7} isLoading={isLoading} error={error} emptyText="Токенов нет" />
            ) : null}
            {showRows &&
              tokens.map((token) => {
                const status = getTokenStatus(token);
                return (
                  <TableRow key={token.id}>
                    <TableCell className="font-medium">{token.name}</TableCell>
                    {all ? <TableCell className="text-sm">{token.adminEmail}</TableCell> : null}
                    <TableCell className="text-sm">{tokenKindLabel(token)}</TableCell>
                    <TableCell>
                      <ScopeBadges scopes={token.scopes} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDateTime(token.lastUsedAt)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{formatDateTime(token.expiresAt)}</TableCell>
                    <TableCell>
                      <TokenStatusBadge status={status} />
                    </TableCell>
                    <TableCell className="text-right">
                      {status === 'active' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => void handleRevoke(token)}
                          disabled={revokeOne.isPending}
                        >
                          Отозвать
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
      {ConfirmationDialog}
    </div>
  );
}
